import {AfterContentInit, Component, ElementRef, EventEmitter, Input, Optional, Output, Self, ViewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NgControl, NgModel, ValidationErrors} from '@angular/forms';
import {IntegerValidatorDirective} from '../input/validators/integer-validator.directive';

@Component({
  selector: 'ds-quantity',
  templateUrl: './quantity.component.html',
  styleUrls: ['./quantity.component.scss'],
})
export class QuantityComponent implements ControlValueAccessor, AfterContentInit {
  @ViewChild('innerInput', {static: true}) innerInput: ElementRef;
  @Input() ariaLabelIncrement: string;
  @Input() ariaLabelDecrement: string;
  @Input() defaultValue = 1;
  @Input() ariaLabelledBy: string;
  /** Object that contains an error message and an Icon class for error */
  @Input() error = {message: '', iconClass: ''};
  @Input() id = 'quantity-input';
  @Input() label: string;
  @Input() showErrorMessages = true;
  @Input() step = 1;
  @Input() min: number;
  @Input() max: number;
  @Input() disableTextInput: boolean;
  @Output() focusEvent = new EventEmitter<boolean>();
  formControl: AbstractControl;
  hasFocus = false;
  innerValue: string;
  disabled: boolean = false;
  readonly: boolean = false;

  get errors(): ValidationErrors | null {
    return this.formControl ? this.formControl.errors : null;
  }

  get value(): string {
    return this.innerValue;
  }

  set value(value) {
    if (value !== undefined && this.value !== value) {
      this.innerValue = value;
      this.onChange(value);
      this.onTouch(value);
    }
  }

  onChange: any = () => {
    // This is intentional
  };

  onTouch: any = () => {
    // This is intentional
  };

  /**
   * Generates an id for the error-message element to connect it to the input
   * (Required for screen readers)
   *
   * @returns the id for the current error-message element
   */
  get errorMessageID(): string {
    return this.id + '_error-message';
  }

  /**
   * Generates an id for the label element to connect it to the input
   * (Required for screen readers)
   *
   * @returns the id for the current label element
   */
  get labelID(): string {
    return this.id + '_label';
  }

  /**
   * Gets the current value as number, and makes sure it is valid.
   * If its not valid, the defaultValue will be returned.
   */
  get valueAsValidNumber(): number {
    if (IntegerValidatorDirective.inputRegex.test(this.value)) {
      return parseInt(this.value, 10);
    }
    return this.defaultValue;
  }

  /**
   * Decides if this input field is valid according to validation attributes.
   *
   * @returns false if the content of this input is valid otherwise true.
   */
  get invalid(): boolean {
    return this.formControl ? this.formControl.invalid : false;
  }

  /**
   * Decides if an error message should be shown for this input field.
   *
   * @returns true if an error message should be shown.
   */
  get showError(): boolean {
    if (this.showErrorMessages) {
      return this.invalid;
    }
    return false;
  }

  /**
   * This component needs a value accessor reference to validate the input with angular validators.
   * Value accessor is not set using the providers to avoid running into a circular import.
   *
   * Further information:
   * https://material.angular.io/guide/creating-a-custom-form-field-control
   * https://stackoverflow.com/questions/44201298
   * https://stackoverflow.com/questions/57186593
   *
   * Please note that for when you use ngModel max and min validators a custom validator should be implemented.
   *
   * @param ngControl used when FormControl is applied to this input
   * @param ngModel used when ngModel is applied to this input
   */
  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() @Self() public ngModel: NgModel
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    if (ngModel) {
      ngModel.valueAccessor = this;
    }
  }

  ngAfterContentInit() {
    this.setFormControl(this.ngControl, this.ngModel);
    if (!this.value) {
      this.value = this.defaultValue.toString(10);
    }
    this.setReadonlyInputText();
  }

  /**
   * Setting the value accessor directly (instead of using the providers)
   * to avoid running into a circular import.
   *
   * @param ngControl used when formcontrol is applied to this input
   * @param ngModel used when ngModel is applied to this input
   */
  setFormControl(ngControl: NgControl, ngModel: NgModel) {
    if (ngModel && ngModel.control) {
      this.formControl = ngModel.control;
    } else if (ngControl && ngControl.control) {
      this.formControl = ngControl.control;
    }
  }

  setHasFocus(isFocused: boolean = true) {
    this.hasFocus = isFocused;
    this.focusEvent.emit(isFocused);
  }

  setFocusState(isFocused: boolean = true) {
    if (isFocused) {
      // makes the dom render before this method is executed.
      setTimeout(() => {
        this.innerInput.nativeElement.focus();
      }, 0);
    } else {
      this.innerInput.nativeElement.blur();
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  subtractStep(currentValue: number, step?: number) {
    const s = step || this.step;
    return currentValue - s;
  }

  addStep(currentValue: number, step?: number) {
    const s = step || this.step;
    return currentValue + s;
  }

  /**
   * Decrements the current number if the subtraction is valid.
   * If the current value is invalid this.defaultValue will be set as new value.
   */
  decrement(): void {
    const nextErrors = this.getErrorsOfValue(this.subtractStep);
    if (nextErrors && nextErrors.min) {
      this.value = nextErrors.min.min.toString(10);
    } else {
      if (nextErrors && nextErrors.max) {
        this.value = nextErrors.max.max.toString(10);
      } else if (!this.containsInteger(this.value)) {
        this.value = this.defaultValue.toString(10);
      } else {
        this.value = this.subtractStep(this.valueAsValidNumber).toString(10);
      }
    }
  }


  isMinusDisabled(): boolean {
    return this.isControlDisabled('min', this.subtractStep);
  }

  /**
   * Increment the current number if the sum is valid.
   * If the current value is invalid this.defaultValue will be set as new value.
   */
  increment(): void {
    const nextErrors = this.getErrorsOfValue(this.addStep);
    if (nextErrors && nextErrors.max) {
      this.value = nextErrors.max.max.toString(10);
    } else {
      if (nextErrors && nextErrors.min) {
        this.value = nextErrors.min.min.toString(10);
      } else if (!this.containsInteger(this.value)) {
        this.value = this.defaultValue.toString(10);
      } else {
        this.value = this.addStep(this.valueAsValidNumber).toString(10);
      }
    }
  }

  isPlusDisabled(): boolean {
    return this.isControlDisabled('max', this.addStep);
  }

  isControlDisabled(errorName, calcFn) {
    if (!IntegerValidatorDirective.inputRegex.test(this.value)) {
      return this.disabled;
    }
    const nextErrors = this.getErrorsOfValue(calcFn);
    return this.disabled || (!!nextErrors && !!nextErrors[errorName]);
  }

  containsInteger(value): boolean {
    return IntegerValidatorDirective.inputRegex.test(value) && '' !== value && '-' !== value;
  }

  /**
   * Validates the value returned by the callback function.
   *
   * @param calcFn method taking in the current value as an argument. Returns a value that will be validated.
   */
  getErrorsOfValue(calcFn): ValidationErrors | null {
    if (this.formControl && this.formControl.validator) {
      const val = calcFn(this.valueAsValidNumber, this.step);
      const mockedControl = {value: val} as AbstractControl;
      return this.formControl.validator(mockedControl);
    }
    return null;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Checks if min and max values for the product are equal, in which case it sets the input to readonly mode (non-editable)
   *
   * @param value optional boolean to be passed in case input value should be made non-editable when min !== max
   */
  setReadonlyInputText(value?: boolean): void {
    if (value !== undefined  && this.readonly !== value) {
      this.readonly = value;
    } else if (this.min !== undefined && this.max !== undefined && this.min === this.max) {
      this.readonly = true;
    }
  }
}
